import { SNAPSHOTS_STORE, allByPrefix, deleteByPrefix, run } from "./db";

/**
 * The last answer to a request one of the offline screens needs (US-33.4, EP-33 decision 4).
 *
 * <p>Not a session and not a token: the application remembers <em>what it was told</em>, per user,
 * so that a tablet waking up in a cellar can draw the sitting it was in the middle of. Tokens stay in
 * memory where ADR-004 put them, and the price of that decision is exactly this — a cold start with
 * no network shows what the screen last knew and refuses every write until somebody signs in again.
 *
 * <p>Keyed by the signed-in user, because the tablet in the cupping room is shared. Reading somebody
 * else's snapshot would be reading their roastery.
 *
 * <p>The second half of the key is the <strong>query key</strong> the application asked with, not a
 * URL, and that is what makes this cheap: what was saved can be put straight back into the query
 * cache on the next cold start, so no screen has to know it is being remembered — it simply finds
 * its answer already there. `["get", "/api/v1/production/board", {…}]`, as JSON.
 */
export interface Snapshot<T = unknown> {
  key: string;
  sub: string;
  /** The query key, as JSON — two dates on the board are two different answers. */
  request: string;
  body: T;
  savedAt: number;
}

function keyOf(sub: string, request: string): string {
  return `${sub}|${request}`;
}

/** The stored form of a query key: stable, because openapi-react-query builds it the same way. */
export function requestKey(queryKey: readonly unknown[]): string {
  return JSON.stringify(queryKey);
}

export async function saveSnapshot(sub: string, request: string, body: unknown): Promise<void> {
  if (!sub || body === undefined) {
    return;
  }
  try {
    await run(SNAPSHOTS_STORE, "readwrite", (store) =>
      store.put({ key: keyOf(sub, request), sub, request, body, savedAt: Date.now() } satisfies Snapshot),
    );
  } catch {
    // Storage off, or full: the screen still has its answer, it simply will not have it tomorrow.
  }
}

/** Everything this user was shown, for putting back into the query cache at start (US-33.4). */
export function allSnapshots(sub: string): Promise<Snapshot[]> {
  return allByPrefix<Snapshot>(SNAPSHOTS_STORE, `${sub}|`);
}

/** When this device last heard from the server — what the banner puts on screen. */
export async function newestSavedAt(sub: string): Promise<number | undefined> {
  const rows = await allSnapshots(sub);
  return rows.length === 0 ? undefined : Math.max(...rows.map((row) => row.savedAt));
}

/** Signing out, or switching the setting off: this user's copy of the roastery leaves the device. */
export async function clearSnapshots(sub: string): Promise<void> {
  await deleteByPrefix(SNAPSHOTS_STORE, `${sub}|`);
}

const KEEP_DATA_KEY = "roastery.offline.keep-data";

/**
 * Whether this device may keep a copy at all — per device, not per user (US-33.4, decision 4).
 *
 * <p>A borrowed tablet, a shared computer in a rented hall: the person who says "not on this one" is
 * saying it about the machine, and the next person to sign in should inherit that answer rather than
 * have to know to repeat it.
 */
export function keepsDataOffline(): boolean {
  try {
    return window.localStorage.getItem(KEEP_DATA_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setKeepsDataOffline(keep: boolean): void {
  try {
    window.localStorage.setItem(KEEP_DATA_KEY, keep ? "on" : "off");
  } catch {
    // A browser that cannot remember the setting cannot keep the data either.
  }
}
